import { useEffect, useRef, useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import api from "@/lib/api";
import { Store, Building2, Plus, Save, Trash2, Loader2, UploadCloud } from "lucide-react";
import { toast } from "sonner";

const ACCENT = "#0f766e";

export default function StoreDirectory() {
  const [dir, setDir] = useState(null);
  const [rows, setRows] = useState([]);
  const [add, setAdd] = useState({ customer_code: "", name: "", sold_to_ref: "28732" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/order-import/directory");
    setDir(data);
    setRows(data.stores.map((s) => ({ ...s })));
  };
  useEffect(() => { load(); }, []);

  const bulkImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/order-import/directory/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.total} stores (${data.added} new, ${data.updated} updated)${data.errors?.length ? ` · ${data.errors.length} skipped` : ""}`);
      await load();
    } catch {
      toast.error("Bulk import failed — check the CSV format");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (r) => {
    setBusy(true);
    try { await api.post("/order-import/directory", { customer_code: r.customer_code, name: r.name, sold_to_ref: r.sold_to_ref }); toast.success(`Saved ${r.customer_code}`); await load(); }
    catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const addStore = async () => {
    if (!add.customer_code || !add.name) { toast.error("Code and name required"); return; }
    setBusy(true);
    try { await api.post("/order-import/directory", add); toast.success(`Added ${add.customer_code}`); setAdd({ customer_code: "", name: "", sold_to_ref: "28732" }); await load(); }
    catch { toast.error("Add failed"); } finally { setBusy(false); }
  };
  const del = async (code) => {
    setBusy(true);
    try { await api.delete(`/order-import/directory/${code}`); toast.success(`Removed ${code} from directory`); await load(); }
    catch { toast.error("Remove failed"); } finally { setBusy(false); }
  };

  if (!dir) return <div className="p-8 text-sm text-[#6a6d70]"><Loader2 className="mr-2 inline animate-spin" size={16} /> Loading directory…</div>;

  return (
    <div data-testid="store-directory">
      <PageHeader title="M&S Store Directory" subtitle="Sold-To account and Ship-To store mappings used by the Order File Import. Add or edit stores so incoming order files resolve to the right customer." icon={Store} accent={ACCENT} />

      <div className="mb-6 rounded-xl border border-[#e5e9f0] bg-white p-4 shadow-sm" data-testid="sd-soldto">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><Building2 size={15} /> Sold-To account</h3>
        <div className="flex flex-wrap gap-2">
          {dir.sold_to.map((c) => (
            <span key={c.customer_code} className="inline-flex items-center gap-2 rounded-lg bg-[#eef1f5] px-3 py-1.5 text-sm"><span className="font-mono font-bold text-[#0f766e]">{c.customer_code}</span> {c.name}</span>
          ))}
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-dashed border-[#c9d2dc] bg-white p-4 shadow-sm" data-testid="sd-add">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><Plus size={15} /> Add store</h3>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-[#6a6d70]">Store code<input data-testid="sd-add-code" value={add.customer_code} onChange={(e) => setAdd({ ...add, customer_code: e.target.value })} placeholder="S1234" className="mt-1 block w-28 rounded border border-[#c8cfda] px-2 py-1 text-sm" /></label>
          <label className="text-xs text-[#6a6d70]">Store name<input data-testid="sd-add-name" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} placeholder="M&S Store 1234" className="mt-1 block w-64 rounded border border-[#c8cfda] px-2 py-1 text-sm" /></label>
          <label className="text-xs text-[#6a6d70]">Sold-To ref<input data-testid="sd-add-soldto" value={add.sold_to_ref} onChange={(e) => setAdd({ ...add, sold_to_ref: e.target.value })} className="mt-1 block w-28 rounded border border-[#c8cfda] px-2 py-1 text-sm" /></label>
          <button data-testid="sd-add-btn" onClick={addStore} disabled={busy} className="rounded-md bg-[#0f766e] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#0b5f58] disabled:opacity-50">Add</button>
          <div className="ml-auto flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" onChange={bulkImport} className="hidden" data-testid="sd-bulk-input" />
            <button data-testid="sd-bulk-btn" onClick={() => fileRef.current?.click()} disabled={busy} className="inline-flex items-center gap-2 rounded-md border border-[#0f766e] px-4 py-1.5 text-sm font-semibold text-[#0f766e] hover:bg-[#f0fdfa] disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Bulk import CSV
            </button>
          </div>
        </div>
        <p className="mt-2 text-xs text-[#8a8d90]">CSV/TSV columns: <span className="font-mono">store_code, store_name, sold_to_ref</span> (header optional; sold_to_ref defaults to 28732). Existing codes are updated.</p>
      </div>

      <div className="overflow-hidden rounded-xl border border-[#e5e9f0] bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wide text-[#6a6d70]">
            <tr className="border-b border-[#eef1f5]"><th className="px-3 py-2">Code</th><th className="px-3 py-2">Store name</th><th className="px-3 py-2">Sold-To ref</th><th className="px-3 py-2 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.customer_code} data-testid={`sd-row-${r.customer_code}`} className="border-b border-[#f2f4f7] last:border-0">
                <td className="px-3 py-2 font-mono font-semibold text-[#DB2777]">{r.customer_code}</td>
                <td className="px-3 py-2"><input value={r.name} onChange={(e) => setRows((x) => x.map((y, j) => (j === i ? { ...y, name: e.target.value } : y)))} className="w-full rounded border border-transparent px-1 py-0.5 hover:border-[#e5e9f0] focus:border-[#0f766e] focus:outline-none" /></td>
                <td className="px-3 py-2"><input value={r.sold_to_ref || ""} onChange={(e) => setRows((x) => x.map((y, j) => (j === i ? { ...y, sold_to_ref: e.target.value } : y)))} className="w-24 rounded border border-transparent px-1 py-0.5 font-mono hover:border-[#e5e9f0] focus:border-[#0f766e] focus:outline-none" /></td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`sd-save-${r.customer_code}`} onClick={() => save(r)} disabled={busy} className="inline-flex items-center gap-1 rounded border border-[#0f766e] px-2 py-1 text-xs font-semibold text-[#0f766e] hover:bg-[#f0fdfa]"><Save size={12} /> Save</button>
                    <button data-testid={`sd-del-${r.customer_code}`} onClick={() => del(r.customer_code)} disabled={busy} className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
